import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import Layout from '../components/Layout';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ArrowRight,
  Save,
  Globe,
  Eye
} from 'lucide-react';

interface TextInputProps {
  label: string;
  path: string;
  placeholder?: string;
  multiline?: boolean;
  textData: any;
  onTextChange: (path: string, value: string) => void;
}

const TextInput: React.FC<TextInputProps> = React.memo(({ 
  label, 
  path, 
  placeholder = '', 
  multiline = false, 
  textData, 
  onTextChange 
}) => {
  const value = path.split('.').reduce((obj: any, key: string) => obj?.[key], textData) || '';
  
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onTextChange(path, e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onTextChange(path, e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      )}
    </div>
  );
});

const TextEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [selectedLanguage, setSelectedLanguage] = useState('hebrew');
  const [textData, setTextData] = useState<any>({});
  const [hasChanges, setHasChanges] = useState(false);
  const queryClient = useQueryClient();

  const { data: event } = useQuery(
    ['event', id],
    () => axios.get(`/api/events/${id}`).then(res => res.data.data),
    { enabled: !!id }
  );

  const { isLoading } = useQuery(
    ['textConfig', id],
    () => axios.get(`/api/texts/event/${id}`).then(res => res.data.data),
    {
      enabled: !!id,
      onSuccess: (data) => {
        setTextData(data.texts);
        setSelectedLanguage(data.language);
      }
    }
  );

  const { data: templates } = useQuery(
    'textTemplates',
    () => axios.get('/api/texts/templates').then(res => res.data)
  );

  const saveTextsMutation = useMutation(
    (data: any) => axios.put(`/api/texts/event/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['textConfig', id]);
        toast.success('טקסטים נשמרו בהצלחה');
        setHasChanges(false);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'שגיאה בשמירת הטקסטים');
      },
    }
  );

  const applyTemplateMutation = useMutation(
    (language: string) => axios.post(`/api/texts/event/${id}/apply-template`, { language }),
    {
      onSuccess: (response) => {
        setTextData(response.data.textConfig.texts);
        setSelectedLanguage(response.data.textConfig.language);
        setHasChanges(true);
        toast.success('תבנית השפה הוחלה בהצלחה');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'שגיאה בהחלת תבנית השפה');
      },
    }
  );

  const handleTextChange = (path: string, value: string) => {
    const keys = path.split('.');
    const newTextData = { ...textData };
    let current = newTextData;
    
    // Get the current value to compare
    const currentValue = keys.reduce((obj: any, key: string) => obj?.[key], textData) || '';
    
    // Only update if the value actually changed
    if (currentValue === value) {
      return;
    }
    
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {};
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    setTextData(newTextData);
    setHasChanges(true);
  };

  const handleSave = () => {
    saveTextsMutation.mutate({
      language: selectedLanguage,
      texts: textData
    });
  };

  const handleApplyTemplate = (language: string) => {
    if (hasChanges) {
      if (!window.confirm('יש לך שינויים שלא נשמרו. האם אתה בטוח שברצונך להחליף לתבנית חדשה?')) {
        return;
      }
    }
    applyTemplateMutation.mutate(language);
  };


  if (isLoading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to={`/events/${id}`}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            >
              <ArrowRight className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">עריכת טקסטים</h1>
              {event && (
                <p className="text-gray-600">{event.name} - {event.coupleNames.bride} & {event.coupleNames.groom}</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {event?.siteUrl && (
              <a
                href={event.siteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Eye className="h-4 w-4 ml-2" />
                תצוגה מקדימה
              </a>
            )}
            
            <button
              onClick={handleSave}
              disabled={!hasChanges || saveTextsMutation.isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4 ml-2" />
              {saveTextsMutation.isLoading ? 'שומר...' : 'שמור שינויים'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Language Templates Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 sticky top-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Globe className="h-5 w-5" />
                תבניות שפה
              </h3>
              
              <div className="space-y-3">
                {templates && Object.keys(templates).map((lang) => (
                  <button
                    key={lang}
                    onClick={() => handleApplyTemplate(lang)}
                    disabled={applyTemplateMutation.isLoading}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                      selectedLanguage === lang
                        ? 'bg-blue-100 text-blue-900 border border-blue-200'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    <div className="font-medium capitalize">{lang}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {lang === 'hebrew' && 'עברית'}
                      {lang === 'english' && 'English'}
                      {lang === 'spanish' && 'Español'}
                    </div>
                  </button>
                ))}
              </div>

              {hasChanges && (
                <div className="mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800">
                    יש לך שינויים שלא נשמרו
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Text Editor */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="space-y-8">
                {/* Welcome Screen */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">
                    מסך ברוכים הבאים
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextInput
                      label="כפתור התחלה"
                      path="welcomeStartButton"
                      placeholder="#התחלנו"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                  </div>
                </div>

                {/* Gallery */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">
                    גלריה
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextInput
                      label="שמות בני הזוג"
                      path="coupleNames"
                      placeholder="Sapir & Amit"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                    <TextInput
                      label="כותרת משנה לחתונה"
                      path="weddingSubtitle"
                      placeholder="-THE WEDDING-"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                  </div>
                </div>

                {/* Photo Editor */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">
                    עורך תמונות
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextInput
                      label="כפתור הדפסה"
                      path="printButton"
                      placeholder="שלח להדפסה"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                  </div>
                </div>

                {/* Print Preview */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">
                    תצוגת הדפסה
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextInput
                      label="כותרת תצוגת הדפסה"
                      path="printPreviewTitle"
                      placeholder="וואו, זה נראה מדהים!"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                    <TextInput
                      label="כותרת משנה"
                      path="printPreviewSubtitle"
                      placeholder="ככה המגנט הולך להיראות"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                    <TextInput
                      label="שאלת אישור"
                      path="printPreviewQuestion"
                      placeholder="משרים את ההדפסה?"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                    <TextInput
                      label="כפתור אישור הדפסה"
                      path="printConfirmButton"
                      placeholder="בזוק, תדפיסו!"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                    <TextInput
                      label="כפתור חזרה"
                      path="backButton"
                      placeholder="חזור"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                  </div>
                </div>

                {/* Print Success */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">
                    מסך הצלחת הדפסה
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextInput
                      label="כותרת הצלחה"
                      path="printSuccessTitle"
                      placeholder="שלחנו את המגנט להדפסה!"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                    <TextInput
                      label="הזמנה לעמדה"
                      path="printSuccessInvite"
                      placeholder="מוזמנים לגשת לעמדת"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                    <TextInput
                      label="איסוף המגנט"
                      path="printSuccessCollect"
                      placeholder="ולאסוף את המגנט שלכם"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                  </div>
                </div>

                {/* General */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">
                    כללי
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextInput
                      label="שם החברה"
                      path="companyName"
                      placeholder="Cheeez"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                    <TextInput
                      label="מונה תמונות"
                      path="photoCounter"
                      placeholder="תמונה {current} מתוך {total}"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                    <TextInput
                      label="לב עליון"
                      path="topHeart"
                      placeholder="♡"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                    <TextInput
                      label="לב תחתון"
                      path="bottomHeart"
                      placeholder="♡"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                  </div>
                </div>

                {/* Stickers */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">
                    מדבקות
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextInput
                      label="כותרת בחירת מדבקות"
                      path="stickerPickerTitle"
                      placeholder="Add Stickers"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                    <TextInput
                      label="קטגוריית אימוג'ים"
                      path="stickerCategories.emojis"
                      placeholder="Emojis"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                    <TextInput
                      label="קטגוריית טקסט"
                      path="stickerCategories.text"
                      placeholder="Text"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                    <TextInput
                      label="קטגוריית מסגרות"
                      path="stickerCategories.frames"
                      placeholder="Frames"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                    <TextInput
                      label="מדבקת שמש"
                      path="stickerNames.sun"
                      placeholder="Sun"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                    <TextInput
                      label="מדבקת לב"
                      path="stickerNames.heart"
                      placeholder="Heart"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                    <TextInput
                      label="מדבקת כוכב"
                      path="stickerNames.star"
                      placeholder="Star"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                    <TextInput
                      label="מדבקת אש"
                      path="stickerNames.fire"
                      placeholder="Fire"
                      textData={textData}
                      onTextChange={handleTextChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TextEditor;
